import * as Y from 'yjs'
import type { DiagramData, DiagramNode, DiagramEdge, DiagramType, Container, DiagramLayer } from '../types/schema'
import { ROOM_MAPS, ROOM_META_KEYS, edgeRoomId } from './roomContract'

/**
 * Room <-> DiagramData bridge.
 *
 * A "room" is one Yjs Y.Doc for one diagram. Per the SHARED ROOM CONTRACT it has
 * these shared types:
 *   - getMap("nodes"):      Y.Map keyed by node.id, value = PLAIN JSON DiagramData node
 *   - getMap("edges"):      Y.Map keyed by edge.id, value = PLAIN JSON DiagramData edge
 *   - getMap("containers"): Y.Map keyed by container.id (RT-1: grouping no longer
 *                           lost across the bridge)
 *   - getMap("meta"):       keys "diagramType" (string), "title" (string?) and
 *                           "layers" (DiagramLayer[]; RT-1: draw.io layers sync)
 *
 * These store plain DiagramData objects (NOT React Flow nodes), so the same
 * room interoperates with the MCP server which speaks DiagramData natively.
 *
 * Writes happen inside ydoc.transact(fn, origin); origin is the STRING "agent"
 * for MCP writes and a per-browser-client unique string for browser writes.
 */

const DEFAULT_DIAGRAM_TYPE: DiagramType = 'architecture'

/** The nodes Y.Map (id -> plain DiagramData node). */
export function getRoomNodesMap(ydoc: Y.Doc): Y.Map<DiagramNode> {
  return ydoc.getMap<DiagramNode>(ROOM_MAPS.nodes)
}

/** The edges Y.Map (id -> plain DiagramData edge). */
export function getRoomEdgesMap(ydoc: Y.Doc): Y.Map<DiagramEdge> {
  return ydoc.getMap<DiagramEdge>(ROOM_MAPS.edges)
}

/** The containers Y.Map (id -> plain DiagramData container). */
export function getRoomContainersMap(ydoc: Y.Doc): Y.Map<Container> {
  return ydoc.getMap<Container>(ROOM_MAPS.containers)
}

/** The meta Y.Map (diagramType, title, layers). */
export function getRoomMetaMap(ydoc: Y.Doc): Y.Map<unknown> {
  return ydoc.getMap<unknown>(ROOM_MAPS.meta)
}

/** The pages Y.Map (RT-8 multi-page rooms): pageId -> RoomPageEntry. */
export function getRoomPagesMap(ydoc: Y.Doc): Y.Map<unknown> {
  return ydoc.getMap<unknown>(ROOM_MAPS.pages)
}

/**
 * Read the room's three maps back into a DiagramData object per the contract.
 */
export function roomToDiagramData(ydoc: Y.Doc): DiagramData {
  const nodesMap = getRoomNodesMap(ydoc)
  const edgesMap = getRoomEdgesMap(ydoc)
  const metaMap = getRoomMetaMap(ydoc)

  const containersMap = getRoomContainersMap(ydoc)

  const diagramType = (metaMap.get(ROOM_META_KEYS.diagramType) as DiagramType | undefined) ?? DEFAULT_DIAGRAM_TYPE
  const title = metaMap.get(ROOM_META_KEYS.title) as string | undefined
  const layers = metaMap.get(ROOM_META_KEYS.layers) as DiagramLayer[] | undefined

  const data: DiagramData = {
    diagramType,
    nodes: Array.from(nodesMap.values()),
    edges: Array.from(edgesMap.values()),
  }
  const containers = Array.from(containersMap.values())
  if (containers.length > 0) {
    data.containers = containers
  }
  if (title !== undefined) {
    data.title = title
  }
  if (Array.isArray(layers) && layers.length > 0) {
    data.layers = layers
  }
  return data
}

/**
 * Write a DiagramData object into the room: upsert every node/edge by id and
 * delete ids that are no longer present, then set meta. All inside a single
 * transaction tagged with `origin`.
 */
export function writeDiagramDataToRoom(
  ydoc: Y.Doc,
  data: DiagramData,
  origin: string,
): void {
  const nodesMap = getRoomNodesMap(ydoc)
  const edgesMap = getRoomEdgesMap(ydoc)
  const containersMap = getRoomContainersMap(ydoc)
  const metaMap = getRoomMetaMap(ydoc)

  const nodes = data.nodes ?? []
  const edges = data.edges ?? []
  const containers = data.containers ?? []
  const nextContainerIds = new Set(containers.map((c) => c.id))

  // Edges may arrive without an id (DiagramEdge.id is optional). The room keys
  // edges by id, so synthesize a stable id from source/target when absent
  // (shared contract helper, used identically by the MCP RoomManager).
  const nextNodeIds = new Set(nodes.map((n) => n.id))
  const nextEdgeIds = new Set(edges.map(edgeRoomId))

  ydoc.transact(() => {
    // Upsert nodes.
    for (const node of nodes) {
      nodesMap.set(node.id, node)
    }
    // Delete nodes no longer present.
    for (const id of Array.from(nodesMap.keys())) {
      if (!nextNodeIds.has(id)) {
        nodesMap.delete(id)
      }
    }

    // Upsert edges.
    for (const edge of edges) {
      const id = edgeRoomId(edge)
      edgesMap.set(id, edge.id ? edge : { ...edge, id })
    }
    // Delete edges no longer present.
    for (const id of Array.from(edgesMap.keys())) {
      if (!nextEdgeIds.has(id)) {
        edgesMap.delete(id)
      }
    }

    // Upsert containers.
    for (const container of containers) {
      containersMap.set(container.id, container)
    }
    // Delete containers no longer present.
    for (const id of Array.from(containersMap.keys())) {
      if (!nextContainerIds.has(id)) {
        containersMap.delete(id)
      }
    }

    // Meta.
    metaMap.set(ROOM_META_KEYS.diagramType, data.diagramType ?? DEFAULT_DIAGRAM_TYPE)
    if (data.title !== undefined) {
      metaMap.set(ROOM_META_KEYS.title, data.title)
    }
    // Layers (draw.io-style) — whole-array value; layers are small + rarely
    // concurrently edited, so LWW on the array is acceptable.
    if (data.layers !== undefined) {
      metaMap.set(ROOM_META_KEYS.layers, data.layers)
    }
  }, origin)
}
